<template>
	<IosProducts
		v-if="isUniversalPaywall"
		:products="products"
		:show-terms="showTerms"
		:products-theme="productsTheme"
		:product-component="productComponent"
		@change="selectProduct"
	/>

	<template v-else>
		<AndroidProducts
			v-if="isAndroid"
			:products="products"
			:show-terms="showTerms"
			:products-theme="productsTheme"
			:product-component="productComponent"
			@change="selectProduct"
		/>
		<IosProducts
			v-else
			:products="products"
			:show-terms="showTerms"
			:products-theme="productsTheme"
			:product-component="productComponent"
			@change="selectProduct"
		/>
	</template>
</template>

<script>
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import useConfig from '@/composition/config.js'
import IosProducts from './ios/IosProducts.vue'
import AndroidProducts from './android/AndroidProducts.vue'
import { computed } from '@vue/reactivity'
import { inject, onMounted } from '@vue/runtime-core'

export default {
	components: { IosProducts, AndroidProducts },
	props: {
		isUniversalPaywall: { type: Boolean, default: false },
		isAndroid: { type: Boolean, default: false },
		showTerms: { type: Boolean, default: true },
		productsTheme: { type: String, default: 'primary' },
		welcomeProduct: { type: Object, default: () => ({}) },
		topProduct: { type: Object, default: () => ({}) },
		bottomProduct: { type: Object, default: () => ({}) }
	},
	emits: ['change'],
	expose: ['products'],
	setup(props, { emit }) {
		const store = useStore()
		const config = useConfig()
		const { t, locale } = useI18n()
		const route = useRoute()
		const amplitude = inject('amplitude')
		const language = computed(() => locale.value)
		const isEng = computed(() => language.value == 'en')

		const hasTrial = Object.prototype.hasOwnProperty.call(route.query, 'has_trial')
		const productComponent = computed(() => config.get({ key: 'paywall.component', default_value: 'Product' }))

		const iosProducts = computed(() => {
			const welcomeProductFromConfig = config.get({ key: 'paywall.welcome_product', default_value: {} })
			const topProductFromConfig = config.get({ key: 'paywall.top_product', default_value: {} })
			const bottomProductFromConfig = config.get({ key: 'paywall.bottom_product', default_value: {} })

			const topProduct = hasTrial
				? {
						id: 'simple_full_price_subscription_3_months',
						text: t('product.quarterly_with_trial_title', { count: 7 }),
						subtitle: `${t('product.price_per_month')} | ${t('product.no_payment_now')}`,
						terms: t('product.quarterly_with_trial_terms'),
						preset: !welcomeProduct
				  }
				: {
						id: 'simple_original_subscription_3_months',
						text: t('product.quarterly_title'),
						subtitle: t('product.price_per_week'),
						terms: t('product.quarterly_terms'),
						preset: false,
						...topProductFromConfig,
						...props.topProduct
				  }

			const welcomeProduct = {
				id: 'simple_original_premium_subscription_12_months',
				text: t('product.welcome_offer'),
				subtitle: t('product.price_per_week'),
				badge: t('product.badge', { count: 60 }),
				terms: t('product.annyally_terms'),
				preset: true,
				popular: true,
				...welcomeProductFromConfig,
				...props.welcomeProduct
			}

			const bottomProduct = {
				id: 'simple_high_price_subscription_12_months',
				text: t('product.annyally_title'),
				subtitle: t('product.price_per_week'),
				badge: t('product.badge', { count: 45 }),
				badge_digits: t('product.badge_digits', { count: 72 }),
				terms: t('product.annyally_terms'),
				preset: false,
				badge_only_digits: false,
				...bottomProductFromConfig,
				...props.bottomProduct
			}

			const productsArray = [topProduct, bottomProduct]

			if (welcomeProductFromConfig) {
				productsArray.splice(1, 0, welcomeProduct)
			}

			return store.getters['products/getData'](productsArray)
		})

		const androidProducts = computed(() => {
			const welcomeProductFromConfig = config.get({ key: 'paywall.welcome_product', default_value: {} })
			const topProductFromConfig = config.get({ key: 'paywall.top_product', default_value: {} })
			const bottomProductFromConfig = config.get({ key: 'paywall.bottom_product', default_value: {} })

			const welcomeProduct = {
				...welcomeProductFromConfig,
				...props.welcomeProduct
			}

			const topProduct = {
				id: 'simple_price_down_subscription_12_months',
				web_name: t('product.simple_premium_annual'),
				text: t('product.annual'),
				subtitle: isEng.value ? t('product.price_per_week') : '[price]',
				badge: !props.isUniversalPaywall && t('product.badge', { count: 83 }),
				price: isEng.value ? t('product.billed_with_price') : '[monthly_price]',
				period: isEng.value ? t('product.period_annually') : t('product.per_month'),
				preset: !props.isUniversalPaywall,
				...topProductFromConfig,
				...props.topProduct
			}

			const bottomProduct = {
				id: 'simple_original_subscription_1_month',
				web_name: t('product.simple_premium_monthly'),
				text: t('product.monthly'),
				subtitle: isEng.value ? t('product.price_per_week') : '[price]',
				price: isEng.value ? t('product.billed_with_price') : undefined,
				period: isEng.value ? t('product.period_monthly') : t('product.per_month'),
				theme: 'orange',
				preset: false,
				...bottomProductFromConfig,
				...props.bottomProduct
			}

			const productsArray = [topProduct, bottomProduct]

			if (props.isUniversalPaywall && welcomeProduct && welcomeProduct.id) {
				productsArray.splice(1, 0, welcomeProduct)
			}

			return store.getters['products/getData'](productsArray)
		})

		const products = computed(() => (props.isAndroid ? androidProducts.value : iosProducts.value))

		onMounted(() => {
			setTimeout(() => {
				amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallShow'), {
					product_ids: products.value.map((p) => p?.id),
					prices: products.value.map((p) => p?.product_price),
					currency: products.value[0]?.currency?.toLowerCase(),
					data_source: 'client'
				})
			}, 3000)
		})

		return {
			selectProduct: (product) => emit('change', product),
			productComponent,
			products
		}
	}
}
</script>
